import { useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Button,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import PostContentAccordionFields from './PostContentAccordionFields';
import Image from './Image';
import { PostContentAccordionProps } from '../types';
import { arrayOrStringToArray } from '../lib/utils';
import { useTranslation } from '../lib/hooks.context';

const YoutubeVideoEmbed = dynamic(() => import('./YoutubeVideoEmbed'));

const resolveUrl = (entry: string | string[], path: string) => {
  const params = path.split(/\?/)[1];
  const paramsString = params ? `?${params}` : '';
  return `/${arrayOrStringToArray(entry).join('/')}${paramsString}`;
};

const getAnchorSlug = (path: string): string | null => path.split('#')[1];

const PostContentAccordion = ({
  data: accordion,
  renderBlocks,
}: PostContentAccordionProps) => {
  const {
    asPath,
    replace,
    query: { entry = '' },
  } = useRouter();
  const [hover, setHover] = useState(-1);
  const t = useTranslation();

  const defaultAnchorSlug = getAnchorSlug(asPath);
  const defaultAccordionSlug = defaultAnchorSlug
    ? accordion.findIndex(({ slug }) => slug === defaultAnchorSlug)
    : undefined;

  const handleClick = (slug: string) => {
    replace(
      {
        pathname: resolveUrl(entry, asPath),
        hash: slug,
      },
      undefined,
      { shallow: true },
    );
  };

  return (
    <Accordion
      defaultIndex={defaultAccordionSlug}
      allowToggle
      variant="content"
    >
      {accordion?.map(({ slug, title, content, image, video: videoSrc }, i) => (
        <AccordionItem key={slug}>
          {({ isExpanded }) => (
            <>
              <Heading variant="h3" as="h3">
                <AccordionButton
                  onClick={() => {
                    handleClick(slug);
                  }}
                  cursor="pointer"
                  py={[5, 5, 5, 7]}
                  px={0}
                  onMouseOver={() => {
                    setHover(i);
                  }}
                  onMouseOut={() => {
                    setHover(-1);
                  }}
                >
                  {title && (
                    <Text flex="1" textAlign="left">
                      {title}
                    </Text>
                  )}
                  <Button
                    as="span"
                    variant="outlineGreen"
                    {...(hover === i ? { 'data-hover': 'true' } : null)}
                  >
                    {isExpanded ? t('hide') : t('show')}
                  </Button>
                </AccordionButton>
              </Heading>
              <AccordionPanel
                pt={3}
                pb={10}
                px={0}
                display="flex"
                flexWrap="wrap"
              >
                <SimpleGrid columns={[1, 1, 2]} spacing={5} width="100%">
                  {(image || videoSrc) && (
                    <Box flex={['0 0 100%', '0 0 100%', '0 0 100%', '0 0 50%']}>
                      <>
                        {videoSrc && (
                          <YoutubeVideoEmbed
                            title={title}
                            videoSrc={videoSrc}
                            isHidden={!isExpanded}
                          />
                        )}
                        {!videoSrc && image?.src && (
                          <Image
                            width="1440"
                            height="960"
                            src={image.src}
                            alt={image.alt || title}
                          />
                        )}
                      </>
                    </Box>
                  )}
                  <Box
                    flex={['0 0 100%', '0 0 100%', '0 0 100%', '0 0 50%']}
                    sx={{ '> *:first-child': { marginTop: '0' } }}
                  >
                    <PostContentAccordionFields
                      renderBlocks={renderBlocks}
                      fields={content || []}
                    />
                  </Box>
                </SimpleGrid>
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      ))}
    </Accordion>
  );
};
export default PostContentAccordion;
